/**
 * SectionWrapper Component
 * Handles section visibility, animations, scroll tracking
 */

import React, { useRef, useEffect } from 'react';
import { useIntersectionObserver } from '../../hooks/useTracking';

const styles = {
  wrapper: {
    width: '100%',
    opacity: 1,
    transition: 'opacity 0.3s ease-in-out',
  },
  wrapperHidden: {
    opacity: 0,
    pointerEvents: 'none',
    display: 'none',
  },
  wrapperAnimatingIn: {
    animation: 'fadeIn 0.4s ease-in-out',
  },
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '0 20px',
  },
};

/**
 * SectionWrapper
 * Wraps section components with visibility, animation, and tracking logic
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Section content
 * @param {string} props.sectionId - Unique section identifier
 * @param {boolean} props.visible - Whether section should be displayed
 * @param {Function} props.onSectionView - Callback when section becomes visible
 * @param {boolean} props.animateIn - Whether to animate in (default: true)
 * @param {number} props.minHeight - Minimum height in pixels
 * @param {string} props.className - Additional CSS classes
 */
const SectionWrapper = React.forwardRef(
  (
    {
      children,
      sectionId,
      visible = true,
      onSectionView = null,
      animateIn = true,
      minHeight = 0,
      className = '',
    },
    ref
  ) => {
    const localRef = useRef(null);
    const finalRef = ref || localRef;
    const hasReportedViewRef = useRef(false);

    // Add CSS animation if not already in page
    useEffect(() => {
      if (!document.querySelector('style[data-section-animations]')) {
        const style = document.createElement('style');
        style.setAttribute('data-section-animations', 'true');
        style.textContent = `
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `;
        document.head.appendChild(style);
      }
    }, []);

    // Trigger view tracking when section becomes visible
    useIntersectionObserver(
      finalRef,
      () => {
        if (visible && onSectionView && !hasReportedViewRef.current) {
          hasReportedViewRef.current = true;
          onSectionView(sectionId);
        }
      },
      { threshold: 0.1 }
    );

    if (!visible) {
      return null;
    }

    const wrapperStyle = {
      ...styles.wrapper,
      ...(animateIn && styles.wrapperAnimatingIn),
      ...(minHeight && { minHeight: `${minHeight}px` }),
    };

    return (
      <section
        ref={finalRef}
        className={`section-wrapper ${sectionId} ${className}`.trim()}
        style={wrapperStyle}
        data-section-id={sectionId}
        aria-label={`${sectionId} section`}
      >
        <div style={styles.container}>{children}</div>
      </section>
    );
  }
);

SectionWrapper.displayName = 'SectionWrapper';

SectionWrapper.propTypes = {
  children: function (props, propName) {
    if (props[propName] == null) {
      return new Error(`${propName} is required`);
    }
  },
  sectionId: function (props, propName) {
    if (typeof props[propName] !== 'string') {
      return new Error(`${propName} must be a string`);
    }
  },
  visible: function (props, propName) {
    if (typeof props[propName] !== 'boolean') {
      return new Error(`${propName} must be a boolean`);
    }
  },
  onSectionView: function (props, propName) {
    if (props[propName] != null && typeof props[propName] !== 'function') {
      return new Error(`${propName} must be a function`);
    }
  },
  animateIn: function (props, propName) {
    if (typeof props[propName] !== 'boolean') {
      return new Error(`${propName} must be a boolean`);
    }
  },
  minHeight: function (props, propName) {
    if (typeof props[propName] !== 'number') {
      return new Error(`${propName} must be a number`);
    }
  },
  className: function (props, propName) {
    if (typeof props[propName] !== 'string') {
      return new Error(`${propName} must be a string`);
    }
  },
};

export default SectionWrapper;
